import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Copy, Users, TrendingUp, Gift, Eye, EyeOff, CheckCircle, XCircle, Calendar, Mail, Phone, RefreshCw, AlertCircle } from 'lucide-react';
import styled from 'styled-components';
import { referralService } from '../services/referralService';

const Referrals = () => {
  const [showReferralCode, setShowReferralCode] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [milestones, setMilestones] = useState(null);

  const userId = localStorage.getItem('id');

  const fetchDashboard = async () => {
    try {
      const response = await referralService.getReferralDashboard(userId);
      
      if (response.status === 200) {
        setDashboardData(response.data.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    }
  };

  const fetchReferrals = async (page = 1, status = 'all') => {
    try {
      setLoading(true);
      const response = await referralService.getReferralsListing(userId, page, pagination.limit, status)
      
      if (response.status === 200) {
        setReferralData(response.data.data);
        setPagination(prev => ({
          ...prev,
          currentPage: response.data.data.pagination.currentPage,
          totalCount: response.data.data.pagination.totalCount,
          totalPages: response.data.data.pagination.totalPages
        }));
        setLoading(false);
      } else {
        setError(response.message);
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to fetch referrals data');
      console.error('Referrals fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await referralService.getReferralsMilestone(userId);

      if (response.status === 200) {
        setMilestones(response.data.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      console.error("Milestone fetch error:", err);
      setError("Failed to fetch milestone data.");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchDashboard(),
        fetchReferrals(pagination.currentPage, statusFilter),
        fetchMilestones()
      ]);
    };
    loadData();
  }, [pagination.currentPage, pagination.limit, statusFilter]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const copyReferralCode = () => {
    const code = dashboardData?.user?.referralCode || '';
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const columns = [
    {
      name: 'Referred User',
      selector: row => (
        <div className="d-flex align-items-center">
          <div className="avatar-circle me-3">
            {row.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div>
            <div className="fw-semibold text-dark">{row.name}</div>
            <small className="text-muted">{row.email}</small>
          </div>
        </div>
      ),
      sortable: true,
      minWidth: '250px'
    },
    {
      name: 'Contact',
      selector: row => (
        <div>
          <div className="d-flex align-items-center mb-1">
            <Phone size={14} className="text-muted me-2" />
            <small>{row.phone}</small>
          </div>
          <div className="d-flex align-items-center">
            <Mail size={14} className="text-muted me-2" />
            <small className="text-muted">{row.email}</small>
          </div>
        </div>
      ),
      sortable: false,
      minWidth: '200px'
    },
    {
      name: 'Join Date',
      selector: row => (
        <div>
          <div className="fw-medium">{row.joinDate}</div>
          <small className="text-muted">{row.joinTime}</small>
        </div>
      ),
      sortable: true,
    },
    {
      name: 'Status',
      selector: row => (
        <div className="d-flex align-items-center">
          {row.status === 'Active' ? (
            <>
              <CheckCircle size={16} className="text-success me-2" />
              <span className="badge bg-success-subtle text-success px-3 py-2 rounded-pill">
                Active
              </span>
            </>
          ) : (
            <>
              <XCircle size={16} className="text-danger me-2" />
              <span className="badge bg-danger-subtle text-danger px-3 py-2 rounded-pill">
                Inactive
              </span>
            </>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      name: 'Contribution',
      selector: row => (
        <div className="text-end">
          <div className="fw-semibold text-success">${row.contribution}</div>
          <small className="text-muted">earned</small>
        </div>
      ),
      sortable: true,
    }
  ];

  const customStyles = {
    head: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #e9ecef',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderRadius: '8px 8px 0 0',
      },
    },
    headCells: {
      style: {
        color: '#495057',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    rows: {
      style: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #f1f3f4',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
      },
    },
    cells: {
      style: {
        padding: '16px 12px',
      },
    },
  };

  if (loading && !dashboardData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <RefreshCw size={48} className="text-primary mb-3 spin" />
        <p className="text-muted">Loading your referral dashboard...</p>
      </div>
    );
  }

    return (
      <ReferralWrapper>
        <div style={{ minHeight: '100vh' }}>
          <div className="container-xxl py-4">
            {/* Header Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="fw-bold text-dark mb-2">Referral Dashboard</h2>
                    <p className="text-muted mb-0">Manage your referrals and track your earnings</p>
                  </div>
                </div>
              </div>
            </div>

            {/* {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                <AlertCircle size={16} className="me-2" />
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            )} */}

            <div className="row mb-4">
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                          <Users className="text-primary" size={24} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h5 className="fw-bold text-dark mb-1">
                          {dashboardData?.stats?.totalReferrals || 0}
                        </h5>
                        <p className="text-muted mb-0 small">Total Referrals</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                          <TrendingUp className="text-success" size={24} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h5 className="fw-bold text-dark mb-1">
                          {dashboardData?.stats?.currentMultiplier || 0}x
                        </h5>
                        <p className="text-muted mb-0 small">Current Multiplier</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                          <Gift className="text-warning" size={24} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h5 className="fw-bold text-dark mb-1">
                          ${(dashboardData?.stats?.totalEarnings || 0).toFixed(2)}
                        </h5>
                        <p className="text-muted mb-0 small">Total Earnings</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="bg-info bg-opacity-10 p-3 rounded-circle">
                          <Calendar className="text-info" size={24} />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h5 className="fw-bold text-dark mb-1">
                          {dashboardData?.nextMilestone?.remaining_referrals || 0}
                        </h5>
                        <p className="text-muted mb-0 small">To Next Milestone</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {milestones && (
                <div className="card border-0 shadow-sm mt-4">
                  <div className="card-body">
                    <h5 className="fw-bold text-dark mb-3">Milestone Progress</h5>
                    <div className="row">
                      {milestones.allMilestones?.map((milestone, idx) => (
                        <div className="col-md-4 mb-3" key={idx}>
                          <div className={`p-3 border rounded ${milestone.status === 'achieved' ? 'bg-success-subtle' : milestone.status === 'current' ? 'bg-warning bg-opacity-10' : 'bg-light'}`}>
                            <h6 className="mb-1">Milestone {milestone.milestone_number}</h6>
                            <p className="mb-1 small text-muted">Requires {milestone.referrals_required} referrals</p>
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${milestone.progress}%` }}
                                aria-valuenow={milestone.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                            <small className="text-muted">
                              {milestone.status.charAt(0).toUpperCase() + milestone.status.slice(1)}
                            </small>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row align-items-center">
                      <div className="col-lg-8">
                        <h5 className="fw-bold text-dark mb-3">Your Referral Code</h5>
                        <div className="d-flex align-items-center mb-3">
                          <div className="input-group">
                            <input 
                              type="text" 
                              className="form-control form-control-lg border-2" 
                              value={showReferralCode ? (dashboardData?.user?.referralCode || '') : '••••••••'}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa', fontFamily: 'monospace', fontSize: '18px', fontWeight: 'bold' }}
                            />
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => setShowReferralCode(!showReferralCode)}
                            >
                              {showReferralCode ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                            <button 
                              className={`btn ${copiedCode ? 'btn-success' : 'btn-primary'}`}
                              onClick={copyReferralCode}
                            >
                              <Copy size={18} className="me-2" />
                              {copiedCode ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        </div>

                      </div>
                      <div className="col-lg-4">
                        <div className="text-center">
                          <div className="bg-primary bg-opacity-10 p-4 rounded-3 mb-3">
                            <h6 className="text-primary mb-2">Next Milestone</h6>
                            <div className="display-6 fw-bold text-primary">
                              {dashboardData?.nextMilestone?.target_referrals || 0}
                            </div>
                            <small className="text-muted">referrals needed</small>
                          </div>
                          <p className="text-muted small mb-0">
                            Get <strong>{referralData?.milestones?.next?.remaining_referrals || 0} more referrals</strong> to unlock <strong>{dashboardData?.nextMilestone?.multiplier_reward || 0}x multiplier</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="btn-group" role="group">
                    <button 
                      type="button" 
                      className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleStatusFilter('all')}
                    >
                      All ({referralData?.user?.totalReferrals || 0})
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${statusFilter === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleStatusFilter('active')}
                    >
                      Active ({referralData?.user?.activeReferrals || 0})
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${statusFilter === 'inactive' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleStatusFilter('inactive')}
                    >
                      Inactive ({referralData?.user?.inactiveReferrals || 0})
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-bottom py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="fw-bold text-dark mb-0">
                        My Referrals ({pagination.totalCount})
                      </h5>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="text-center py-5">
                        <RefreshCw size={32} className="text-primary mb-3 spin" />
                        <p className="text-muted">Loading referrals...</p>
                      </div>
                    ) : (
                      <DataTable
                        columns={columns}
                        data={referralData?.referrals || []}
                        pagination
                        paginationServer
                        paginationTotalRows={pagination.totalCount}
                        paginationPerPage={pagination.limit}
                        paginationCurrentPage={pagination.currentPage}
                        onChangePage={handlePageChange}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        onChangeRowsPerPage={(newLimit) => {
                          setPagination(prev => ({
                            ...prev,
                            limit: newLimit,
                            currentPage: 1
                          }));
                        }}


                        striped={false}
                        responsive
                        highlightOnHover
                        customStyles={customStyles}
                        noDataComponent={
                          <div className="text-center py-5">
                            <Users size={48} className="text-muted mb-3" />
                            <h6 className="text-muted">
                              {statusFilter === 'all' 
                                ? 'No referrals yet' 
                                : `No ${statusFilter} referrals`}
                            </h6>
                            <p className="text-muted small">
                              {statusFilter === 'all' 
                                ? 'Share your referral code to start earning!' 
                                : `Try changing the filter to see other referrals.`}
                            </p>
                          </div>
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {referralData?.isNewUser && (
              <div className="row mt-4">
                <div className="col-12">
                  <div className="alert alert-info">
                    <div className="d-flex align-items-center">
                      <Gift size={24} className="text-info me-3" />
                      <div>
                        <h6 className="alert-heading mb-1">Welcome to the Referral Program!</h6>
                        <p className="mb-0">
                          Start sharing your referral code to earn rewards. Each successful referral increases your multiplier!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ReferralWrapper>
    );
  };
export default Referrals;

const ReferralWrapper = styled.div`
  .avatar-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 16px;
  }
  
  .card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  .card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
  }
  
  .btn {
    transition: all 0.2s ease;
  }
  
  .input-group .form-control:focus {
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
  }
  
  .bg-success-subtle {
    background-color: #d1e7dd !important;
  }
  
  .bg-danger-subtle {
    background-color: #f8d7da !important;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;